import React, { Component } from 'react';
import PropTypes from 'prop-types';

const propTypes = {
  spritePath: PropTypes.string.isRequired,
  spriteConfigs: PropTypes.oneOfType([PropTypes.object]).isRequired,
  onError: PropTypes.func,
  onLoad: PropTypes.func,
  className: PropTypes.string,
  styles: PropTypes.oneOfType([PropTypes.object]),
  scale: PropTypes.number,
  canAnim: PropTypes.func,
  moveDirection: PropTypes.string,
  moveLoopDirection: PropTypes.string,
  changeCharacter: PropTypes.func,
  newCharacter: PropTypes.string,
  loopAnim: PropTypes.bool,
  spriteUpdated: PropTypes.number,
  stopMove: PropTypes.bool,
  hasStopped: PropTypes.func,
  moveOnce: PropTypes.bool,
  jumpToAttackMove: PropTypes.bool,
  freezeSpriteFrame: PropTypes.string,
};

const defaultProps = {
  scale: 1,
  className: '',
  styles: {},
  newCharacter: '',
  moveDirection: '',
  moveLoopDirection: '',
  freezeSpriteFrame: '',
  loopAnim: false,
  spriteUpdated: 0,
  stopMove: false,
  moveOnce: false,
  jumpToAttackMove: false,
  hasStopped: () => {},
  changeCharacter: () => {},
  canAnim: () => {},
  onError: () => {},
  onLoad: () => {},
};

class SpritesheetAnim extends Component {
  static loadImage(url = '', callback = () => {}) {
    const img = new Image();

    img.onload = () => {
      callback(null, img);
    };

    img.onerror = err => {
      callback(err);
    };

    img.src = url;
  }

  constructor(props) {
    super(props);

    const { spriteConfigs } = this.props;

    this.state = {
      spriteWidth: 0,
      spriteHeight: 0,
      loopSpriteConfigs: spriteConfigs,
      updatedDirection: '',
    };

    this.unmounting = false;
    this.loopAnimationsST = null;
  }

  componentDidMount() {
    const { loopAnim } = this.props;
    this.loadSprite();

    if (loopAnim) {
      this.loopScript();
    }
  }

  componentDidUpdate(prevProps) {
    const {
      moveDirection,
      moveLoopDirection,
      spritePath,
      spriteConfigs,
      loopAnim,
      spriteUpdated,
      moveOnce,
      jumpToAttackMove,
      freezeSpriteFrame,
    } = this.props;
    const { updatedDirection } = this.state;

    if (prevProps.spritePath !== spritePath) {
      this.loadSprite();
    }

    if (moveDirection !== '') {
      this.animateScript(moveDirection);
    }

    if (prevProps.moveOnce !== moveOnce && moveOnce) {
      this.moveOnce();
    }

    if (prevProps.jumpToAttackMove !== jumpToAttackMove && jumpToAttackMove) {
      this.jumpToAttackMove();
    }

    if (
      moveLoopDirection !== ''
      && updatedDirection !== moveLoopDirection
      && loopAnim
    ) {
      this.changeLoopMovimentDirection(moveLoopDirection);
      clearInterval(this.loopAnimationsST);
      this.loopScript(moveLoopDirection);
    }

    if (prevProps.spriteUpdated !== spriteUpdated && loopAnim) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ loopSpriteConfigs: spriteConfigs }, () => {
        clearInterval(this.loopAnimationsST);
        this.loopScript();
      });
    }

    if (prevProps.freezeSpriteFrame !== freezeSpriteFrame && freezeSpriteFrame !== '') {
      const { style } = this.sprite;

      style.backgroundPosition = `-${freezeSpriteFrame}px 0px`;
    }
  }

  componentWillUnmount() {
    this.unmounting = true;
    clearInterval(this.loopAnimationsST);
  }

  changeLoopMovimentDirection = direction => this.setState({ updatedDirection: direction });

  animateScript = moveDirection => {
    const {
      canAnim,
      spriteConfigs,
      newCharacter,
      changeCharacter,
      stopMove,
    } = this.props;
    const {
      interval,
      width,
      moveLeftStart,
      moveLeftEnd,
      moveRightStart,
      moveRightEnd,
    } = spriteConfigs;
    const diff = width;
    const { style } = this.sprite;
    let position;
    let initFrame;
    let endFrame;

    if (moveDirection === 'left') {
      initFrame = moveLeftStart;
      endFrame = moveLeftEnd;
    } else if (moveDirection === 'right') {
      initFrame = moveRightStart;
      endFrame = moveRightEnd;
    }

    if (stopMove) {
      style.backgroundPosition = `-${initFrame}px 0px`;
      canAnim();

      return;
    }

    position = initFrame;

    const tID = setInterval(() => {
      style.backgroundPosition = `-${position}px 0px`;

      if (position < endFrame) {
        position += diff;
      } else {
        clearInterval(tID);
        style.backgroundPosition = `-${initFrame}px 0px`;

        canAnim();

        if (newCharacter !== '') {
          changeCharacter(newCharacter);
        }
      }
    }, interval);
  };

  loopScript = moveDirection => {
    const { loopSpriteConfigs } = this.state;
    const {
      interval,
      start,
      end,
      width,
      moveLeftStart,
      moveLeftEnd,
      moveRightStart,
      moveRightEnd,
    } = loopSpriteConfigs;
    const diff = width;
    const { style } = this.sprite;
    let position;
    let initFrame;
    let endFrame;

    if (moveDirection === 'left') {
      initFrame = moveLeftStart;
      endFrame = moveLeftEnd;
    } else if (moveDirection === 'right') {
      initFrame = moveRightStart;
      endFrame = moveRightEnd;
    } else {
      initFrame = start;
      endFrame = end;
    }

    position = initFrame;

    this.loopAnimationsST = setInterval(() => {
      style.backgroundPosition = `-${position}px 0px`;

      if (position < endFrame) {
        position += diff;
      } else {
        style.backgroundPosition = `-${initFrame}px 0px`;
        position = initFrame;
      }
    }, interval);
  };

  moveOnce = () => {
    const { spriteConfigs, hasStopped } = this.props;
    const {
      interval, width, start, end,
    } = spriteConfigs;
    const diff = width;
    const { style } = this.sprite;
    let position;

    position = start;

    const moveAnimationOnce = setInterval(() => {
      style.backgroundPosition = `-${position}px 0px`;

      if (position < end) {
        position += diff;
      } else {
        clearInterval(moveAnimationOnce);
        hasStopped();
        style.backgroundPosition = `-${end}px 0px`;
      }
    }, interval);
  };

  jumpToAttackMove = () => {
    const { spriteConfigs, hasStopped } = this.props;
    const {
      interval, width, start, moveLeftEnd, endAttackPos,
    } = spriteConfigs;
    const diff = width;
    const { style } = this.sprite;
    let position;

    position = moveLeftEnd;

    const jumpToAttack = setInterval(() => {
      style.backgroundPosition = `-${position}px 0px`;

      if (position < endAttackPos) {
        position += diff;
      } else {
        clearInterval(jumpToAttack);
        hasStopped();
        style.backgroundPosition = `-${start}px 0px`;
      }
    }, interval + 60);
  };

  loadSprite = () => {
    const { spritePath, onError, onLoad } = this.props;
    const { hasErrored } = this.state;

    if (!hasErrored) {
      SpritesheetAnim.loadImage(spritePath, (err, image) => {
        if (this.unmounting) {
          return;
        }

        if (err) {
          onError(err);

          this.setState({
            hasErrored: true,
          });

          return;
        }

        onLoad();

        this.setState({
          isLoaded: true,
          spriteWidth: image.width,
          spriteHeight: image.height,
        });
      });
    }
  };

  render() {
    const {
      spritePath,
      spriteConfigs,
      className,
      scale,
      styles,
      loopAnim,
    } = this.props;
    const {
      spriteWidth,
      spriteHeight,
      isLoaded,
      loopSpriteConfigs,
    } = this.state;
    const { width, height } = loopAnim ? loopSpriteConfigs : spriteConfigs;

    const blockStyle = {
      backgroundImage: isLoaded ? `url(${spritePath})` : null,
      backgroundPosition: '0px 0px',
      backgroundSize: `${spriteWidth / scale}px ${spriteHeight / scale}px`,
      width: `${width / scale}px`,
      height: `${height / scale}px`,
      position: 'relative',
      ...styles,
    };

    return (
      <div
        id="sprite"
        ref={node => {
          this.sprite = node;
        }}
        className={className}
        style={blockStyle}
      />
    );
  }
}

SpritesheetAnim.propTypes = propTypes;
SpritesheetAnim.defaultProps = defaultProps;

export default SpritesheetAnim;
